"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations, useLocale } from "use-intl";

import { StoriesIcon } from "@/components/appIcons";
import { getWikipediaResourceQueryOptions } from "@/components/wikipedia-resource-query";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { orpc } from "@/utils/orpc.client";

interface WikipediaSectionProps {
  wikidataId: string | null | undefined;
}

export function WikipediaSection({ wikidataId }: WikipediaSectionProps) {
  const t = useTranslations("shared.wikipedia");
  const tBio = useTranslations("shared.bioText");
  const locale = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const resourceWikidataId = wikidataId ?? "";
  const computeWikipediaResource = useCallback(
    () => orpc.music.computeWikipediaResource.call({ wikidataId: resourceWikidataId, locale }),
    [resourceWikidataId, locale],
  );
  const { value: extract } = useDeferredResourceQuery({
    queryOptions: getWikipediaResourceQueryOptions({
      wikidataId: resourceWikidataId,
      locale,
      enabled: !!wikidataId,
    }),
    compute: computeWikipediaResource,
  });

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
  }, [extract]);

  if (!wikidataId || !extract) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 mb-2">
        <StoriesIcon className="text-sm text-muted-foreground" />
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
          {t("title")}
        </p>
      </div>
      <div className="relative">
        <p
          ref={textRef}
          className={`text-justify text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap hyphens-auto md:text-left ${!expanded ? "line-clamp-4" : ""}`}
        >
          {extract.extract}
        </p>
        {isClamped && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-primary hover:underline mt-1"
          >
            {expanded ? tBio("showLess") : tBio("showMore")}
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        <a
          href={extract.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-6 items-center hover:underline max-sm:min-h-11"
        >
          {t("source")}
        </a>
      </p>
    </div>
  );
}
